import { evaluarInforme, formatearImporte, formatearPorcentaje, fechaLegible, esImporte } from './informeFinancieroUtils.js';

/**
 * Genera y descarga el PDF íntegramente en el navegador.
 * No hace llamadas a servicios externos ni registra cifras por consola.
 * Requiere: npm install jspdf jspdf-autotable
 */
export async function generarInformeEjecutivo(datos) {
  // Carga diferida: el dashboard no descarga estas librerías hasta pulsar el botón.
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const v = evaluarInforme(datos);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 19;
  const textW = pageW - marginX * 2;
  let y = 22;

  const C = {
    ink: [27, 43, 59],
    navy: [18, 42, 65],
    teal: [15, 117, 126],
    gray: [84, 98, 110],
    pale: [237, 244, 246],
    line: [214, 224, 230],
    alert: [134, 72, 22],
  };

  function nuevaPagina() {
    pdf.addPage();
    y = 23;
    pdf.setDrawColor(...C.line);
    pdf.setLineWidth(0.3);
    pdf.line(marginX, 16, pageW - marginX, 16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...C.navy);
    pdf.setFontSize(8);
    pdf.text('ALFATEX | INFORME EJECUTIVO FINANCIERO', marginX, 13);
  }

  function reservar(mm) {
    if (y + mm > pageH - 24) nuevaPagina();
  }

  function tituloSeccion(titulo) {
    reservar(16);
    y += 5;
    pdf.setFillColor(...C.teal);
    pdf.rect(marginX, y - 4, 1.4, 8.3, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11.5);
    pdf.setTextColor(...C.navy);
    pdf.text(titulo, marginX + 5, y + 2);
    y += 11;
  }

  function parrafo(texto) {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...C.ink);
    pdf.setFontSize(9.7);
    const lines = pdf.splitTextToSize(texto, textW);
    const alto = lines.length * 5 + 3;
    reservar(alto);
    pdf.text(lines, marginX, y);
    y += alto;
  }

  function tabla(columnas, filas, widths) {
    reservar(25);
    autoTable(pdf, {
      startY: y,
      head: [columnas],
      body: filas,
      theme: 'grid',
      margin: { left: marginX, right: marginX, top: 21, bottom: 24 },
      styles: { font: 'helvetica', fontSize: 8.8, textColor: C.ink, cellPadding: 2.5, overflow: 'linebreak', lineColor: C.line, lineWidth: 0.15 },
      headStyles: { fillColor: C.navy, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 251] },
      columnStyles: widths,
    });
    y = pdf.lastAutoTable.finalY + 3;
  }

  function valor(periodo, clave) {
    return formatearImporte(periodo?.[clave]);
  }

  // Portada compacta.
  pdf.setFillColor(...C.navy);
  pdf.rect(0, 0, pageW, 10, 'F');
  pdf.setTextColor(...C.teal);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('INFORME PARA ENTIDAD FINANCIERA', marginX, 24);
  pdf.setTextColor(...C.navy);
  pdf.setFontSize(22);
  pdf.text('Resumen ejecutivo', marginX, 35);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...C.gray);
  pdf.setFontSize(10);
  const nombre = v.empresa.nombre || 'Empresa pendiente de identificar';
  const nombreLines = pdf.splitTextToSize(nombre, textW);
  pdf.text(nombreLines, marginX, 44);
  y = 44 + nombreLines.length * 5 + 6;
  pdf.setFontSize(9);
  pdf.text(`Fecha de corte: ${fechaLegible(v.empresa.fechaCorte)}`, marginX, y);
  y += 8;
  if (v.borrador) {
    pdf.setTextColor(...C.alert);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('BORRADOR - FALTAN DATOS PARA PRESENTACION BANCARIA', marginX, y);
    y += 8;
  }
  pdf.setDrawColor(...C.line);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 4;

  tituloSeccion('1. Situación y evolución económica');
  if (v.positivos) {
    parrafo('Los resultados del ejercicio 2025 y del acumulado de 2026 son positivos según el resultado conjunto de gestión de Ennage y Genaen. Esta evolución debe analizarse junto con la tesorería disponible, las necesidades de circulante y la capacidad de devolución de la financiación.');
  } else {
    parrafo('El análisis integra el resultado contable de Ennage y el resultado de gestión de Genaen (ventas menos compras y gastos). Los resultados de los ejercicios 2025 y 2026 se muestran a continuación sin anticipar conclusiones de rentabilidad que no estén acreditadas por las cifras disponibles.');
  }
  tabla(
    ['Indicador', '2025 (cierre por validar)', '2026 acumulado'],
    [
      ['Facturación', valor(v.p2025, 'facturacionTotal'), valor(v.p2026, 'facturacionTotal')],
      ['Resultado contable Ennage', valor(v.p2025, 'resultadoEnnage'), valor(v.p2026, 'resultadoEnnage')],
      ['Resultado de gestión Genaen', valor(v.p2025, 'resultadoGenaen'), valor(v.p2026, 'resultadoGenaen')],
      ['Resultado conjunto de gestión', valor(v.p2025, 'resultadoConjunto'), valor(v.p2026, 'resultadoConjunto')],
      ['EBITDA', valor(v.p2025, 'ebitda'), valor(v.p2026, 'ebitda')],
      ['Fondo de maniobra', valor(v.p2025, 'fondoManiobra'), valor(v.p2026, 'fondoManiobra')],
      ['Deuda financiera', valor(v.p2025, 'deudaFinanciera'), valor(v.p2026, 'deudaFinanciera')],
    ],
    { 0: { cellWidth: 62 }, 1: { cellWidth: 54 }, 2: { cellWidth: 56 } },
  );
  parrafo('Nota: 2026 presenta datos acumulados hasta la fecha de corte y no corresponde necesariamente a un ejercicio anual completo. La comparación interanual requiere tener en cuenta esta diferencia temporal.');

  tituloSeccion('2. Transformación operativa ya realizada');
  parrafo('En un entorno especialmente exigente para las pequeñas empresas textiles, la sociedad ha priorizado la eficiencia de sus procesos y el control del gasto. Ha implantado herramientas de gestión complementarias: AlfaWin ERP para administración, facturación y contabilidad; Gestión de Moda para pedidos e inventario; y Alfatex para producción y análisis de información operativa y económica.');
  parrafo('La estrategia tecnológica permite trabajar con una estructura ajustada, limitar tareas repetitivas, mejorar la trazabilidad y disponer de información para tomar decisiones. La financiación no se plantea como sustitución de este esfuerzo de reorganización, sino como apoyo a su funcionamiento productivo.');

  tituloSeccion('3. Criterios de fabricación y rentabilidad');
  parrafo('La coyuntura y las nuevas exigencias del sector hacen necesario evaluar cada fabricación antes de comprometer recursos. La compañía analiza los costes asociados a los pedidos y sus márgenes esperados para priorizar operaciones económicamente justificadas. Los importes siguientes son previsiones de la cartera confirmada, no resultados realizados.');
  tabla(
    ['Cartera comercial y producción', 'Importe / indicador'],
    [
      ['Pedidos firmados pendientes de servir', formatearImporte(v.pedidos.importeFirmado)],
      ['Coste previsto de producción', formatearImporte(v.pedidos.costeProduccionPrevisto)],
      ['Margen bruto previsto', formatearImporte(v.margen)],
      ['Margen bruto previsto (%)', formatearPorcentaje(v.margenPct)],
      ['Anticipos de clientes disponibles', formatearImporte(v.pedidos.anticiposClientes)],
    ],
    { 0: { cellWidth: 113 }, 1: { cellWidth: 59 } },
  );
  parrafo('El margen bruto estimado se obtiene de ingresos de pedidos menos costes de producción reportados, salvo que el dashboard proporcione un margen bruto calculado explícitamente. No equivale al beneficio neto ni garantiza liquidez; deben considerarse también costes generales, impuestos y gastos financieros.');

  tituloSeccion('4. Necesidad de circulante y solicitud financiera');
  parrafo('Los costes de materiales y fabricación pueden vencer antes del cobro de los pedidos. Esta diferencia temporal genera necesidades de circulante que deben dimensionarse mediante un calendario de entradas y salidas de efectivo. El fondo de maniobra contable es una referencia relevante, pero no equivale al efectivo disponible ni determina por sí solo la necesidad de financiación.');
  tabla(
    ['Necesidad financiera', 'Importe'],
    [
      ['Necesidad máxima de tesorería prevista', formatearImporte(v.pedidos.necesidadPicoTesoreria)],
      ['Financiación solicitada', formatearImporte(v.pedidos.financiacionSolicitada)],
      ['Plazo previsto de financiación', v.pedidos.plazoMeses ? `${v.pedidos.plazoMeses} meses` : 'Por definir'],
    ],
    { 0: { cellWidth: 113 }, 1: { cellWidth: 59 } },
  );
  parrafo('La solicitud se vincula a una cartera de pedidos confirmados y a la planificación de producción, facturación y cobro. La capacidad de devolución debe comprobarse con el flujo de caja proyectado, incluyendo los vencimientos de deuda existente y los posibles retrasos de cobro.');

  tituloSeccion('5. Conclusión y finalidad de la financiación');
  if (v.positivos) {
    parrafo('La empresa presenta un resultado conjunto de gestión positivo en ambos periodos examinados, ha realizado un esfuerzo de modernización y aplica criterios de control de costes y rentabilidad previa a la fabricación. El objetivo de la financiación solicitada es acompañar la ejecución de pedidos ya confirmados y cubrir el desfase entre pagos productivos y cobros comerciales, conforme a un plan de tesorería verificable.');
  } else {
    parrafo('La empresa ha realizado un esfuerzo de modernización y aplica criterios de control de costes y análisis previo a la fabricación. El objetivo de la financiación solicitada es cubrir el desfase temporal entre pagos productivos y cobros comerciales. La valoración definitiva de la viabilidad y capacidad de devolución queda supeditada a la validación de las cifras.');
  }
  if (v.borrador) {
    parrafo(`PENDIENTE DE COMPLETAR ANTES DE PRESENTAR: ${v.errores.join('; ')}.`);
  }
  if (Array.isArray(datos.observaciones) && datos.observaciones.length) {
    tituloSeccion('6. Alcance y fuentes pendientes de validar');
    datos.observaciones.forEach(parrafo);
  }

  const totalPaginas = pdf.internal.getNumberOfPages();
  for (let n = 1; n <= totalPaginas; n += 1) {
    pdf.setPage(n);
    pdf.setDrawColor(...C.line);
    pdf.setLineWidth(0.3);
    pdf.line(marginX, pageH - 18, pageW - marginX, pageH - 18);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...C.gray);
    pdf.setFontSize(7.6);
    pdf.text('Confidencial | Uso reservado a la entidad destinataria', marginX, pageH - 12);
    pdf.text(`${n} / ${totalPaginas}`, pageW - marginX, pageH - 12, { align: 'right' });
  }

  const base = (v.empresa.nombre || 'empresa')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 46);
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(v.empresa.fechaCorte || '')
    ? v.empresa.fechaCorte : 'sin_fecha';
  const fileName = `Informe_Ejecutivo_${base}_${fecha}${v.borrador ? '_BORRADOR' : ''}.pdf`;
  pdf.save(fileName);
  return { fileName, borrador: v.borrador, advertencias: v.errores };
}
